import { queriesPayrollRuns } from "@query";
import { PayrollRun, WorkLog } from "@wingspanhq/wingspan-api-sdk";

import { selectorPayrollRunForPayPeriod } from "../../PayrollRuns/selectors/selectorPayrollRunForPayPeriod";
import { useQueryWorkLogConvertedResource } from "./useQueryWorkLogConvertedResource";

/**
 * Resolves the payroll run a work log belongs to.
 *
 * Two sources, in order of precision:
 *
 *  1. **The pay statement it converted into.** An approved W-2 work log names its
 *     run exactly through `payStatement.payrollRunId` — right even when the pay
 *     period holds several runs (an on-cycle one plus off-cycle ones).
 *  2. **Period matching.** Before approval there is no statement, so the
 *     account's runs are matched on `payPeriodId` client-side: `listPayrollRuns`
 *     exposes no `payPeriodId` filter (only type/status/cycleType/payDate/
 *     externalId). `useQueryAll` makes that correct — the whole set is loaded,
 *     not a page of it.
 *
 * Deliberately unfiltered by `type` **and** `status`: this serves both contractor
 * and employee work logs, and a closed/paid run still has to be nameable (the
 * approval-expired state is defined by exactly that). Pinning `filter[type]` is
 * what made work logs of the other payroll type read as having no run at all —
 * and the filter is not required by the endpoint.
 *
 * TODO(v3): add a `payPeriodId` filter to `listPayrollRuns`, or expose
 * `payrollRunId` on `WorkLog` (`listWorkLogs` already filters on it), to drop the
 * full fetch.
 */
export const useQueryWorkLogPayrollRun = (
  workLog?: WorkLog
): { payrollRun: PayrollRun | undefined; isLoading: boolean } => {
  const payPeriodId = workLog?.payPeriodId ?? undefined;

  const { payStatement, queryPayStatement } =
    useQueryWorkLogConvertedResource(workLog);
  const payStatementPayrollRunId = payStatement?.payrollRunId;

  const queryPayrollRun = queriesPayrollRuns.getPayrollRun.useQuery(
    { payrollRunId: payStatementPayrollRunId as string },
    { enabled: !!payStatementPayrollRunId }
  );

  // Held back until the statement read has settled. While it is in flight
  // `payStatementPayrollRunId` is undefined, so starting here would fetch the
  // account's entire run history for an approved work log only to throw it away
  // the moment `getPayrollRun` answers. (A disabled query is not
  // `isInitialLoading`, so a work log with no converted resource does not wait.)
  const queryPayrollRuns = queriesPayrollRuns.listPayrollRuns.useQueryAll(
    {},
    {
      enabled:
        !!payPeriodId &&
        !payStatementPayrollRunId &&
        !queryPayStatement.isInitialLoading
    }
  );

  const payrollRun =
    queryPayrollRun.data ??
    selectorPayrollRunForPayPeriod({
      payrollRuns: queryPayrollRuns.flatData,
      payPeriodId
    });

  return {
    payrollRun,
    // `isInitialLoading`, not `isLoading`: react-query v4 leaves a *disabled*
    // query in `status: "loading"` forever, so `isLoading` never goes false for
    // a work log with no pay period — and a contractor work log never has one.
    isLoading:
      queryPayStatement.isInitialLoading ||
      queryPayrollRun.isInitialLoading ||
      queryPayrollRuns.isInitialLoading
  };
};
